/**
 * ============================================================================
 * TOUR DEFINITIONS SERVICE
 * ============================================================================
 *
 * Provides contextual tour configurations based on farm state.
 * Integrates with FarmStore to detect farm state and recommend appropriate tours.
 *
 * Tours available:
 * - No Farms Tour: Onboarding for users without any farms
 * - Farm Selection Tour: Guide for users with farms but none selected
 * - Crop Farm Setup Tour: Setup guide for crop-focused farms
 * - Livestock Farm Setup Tour: Setup guide for livestock-focused farms
 * - Mixed Farm Setup Tour: Setup guide for mixed operation farms
 * - Task Workflow Tour: Explains the task lifecycle (create → assign → complete → draft)
 * - Transactions Intro Tour: Introduction to financial tracking
 * - AI Assistant Intro Tour: Introduction to AI assistant features
 * - Add Farm Members Tour: Guide for inviting team members
 * - Financial Intro Tour: Comprehensive financial features introduction
 *
 * @module tour
 * @version 1.0.0
 * @created 2026-01-08
 */

import { computed, inject, Injectable } from '@angular/core';

import { FarmStore } from '../../store/farm.store';
import { LayoutStore } from '../../store/layout.store';
import { FarmType } from '../../entities/farm/farm.enums';
import { TourConfig, TourStep } from './tour.models';

// =============================================================================
// TOUR IDS
// =============================================================================

export const TOUR_IDS = {
  NO_FARMS_WELCOME: 'no-farms-welcome',
  FARM_SELECTION: 'farm-selection',
  CROP_FARM_SETUP: 'crop-farm-setup',
  LIVESTOCK_FARM_SETUP: 'livestock-farm-setup',
  MIXED_FARM_SETUP: 'mixed-farm-setup',
  // Feature-specific tours
  TASK_WORKFLOW: 'task-workflow',
  TRANSACTIONS_INTRO: 'transactions-intro',
  AI_ASSISTANT_INTRO: 'ai-assistant-intro',
  ADD_FARM_MEMBERS: 'add-farm-members',
  FINANCIAL_INTRO: 'financial-intro',
} as const;

export type TourId = (typeof TOUR_IDS)[keyof typeof TOUR_IDS];

// =============================================================================
// TOUR DEFINITIONS SERVICE
// =============================================================================

/**
 * Service that provides contextual tour configurations based on farm state.
 * Uses computed signals to reactively determine which tours are available
 * and which tour is recommended for the current user state.
 */
@Injectable({ providedIn: 'root' })
export class TourDefinitionsService {
  private readonly farmStore = inject(FarmStore);
  private readonly layoutStore = inject(LayoutStore);

  // ===========================================================================
  // COMPUTED SIGNALS
  // ===========================================================================

  /**
   * Computed signal for the recommended tour based on current farm state.
   * Returns null if no tour is recommended (e.g., user has completed all tours).
   *
   * Priority order:
   * 1. No farms - welcome tour
   * 2. Has farms but none selected - farm selection tour
   * 3. Farm selected - AI assistant intro tour
   * 4. Farm type setup tour (crop/livestock/mixed)
   * 5. Team tour (add farm members)
   * 6. Financial intro tour
   */
  readonly recommendedTour = computed((): TourConfig | null => {
    const farms = this.farmStore.farms();
    const currentFarm = this.farmStore.currentFarm();
    const completedTours = this.layoutStore.getCompletedTours();

    // Priority 1: No farms
    if (!farms || farms.length === 0) {
      if (!completedTours.includes(TOUR_IDS.NO_FARMS_WELCOME)) {
        return this.getNoFarmsTour();
      }
    }

    // Priority 2: Has farms, none selected
    if (farms && farms.length > 0 && !currentFarm) {
      if (!completedTours.includes(TOUR_IDS.FARM_SELECTION)) {
        return this.getFarmSelectionTour();
      }
    }

    // Priority 3: Farm selected - AI tour
    if (currentFarm && !completedTours.includes(TOUR_IDS.AI_ASSISTANT_INTRO)) {
      return this.getAiAssistantIntroTour();
    }

    // Priority 4: Farm type setup tour
    if (currentFarm) {
      const farmType = currentFarm.type;
      const setupTourId = this.getSetupTourIdByType(farmType);
      if (setupTourId && !completedTours.includes(setupTourId)) {
        return this.getSetupTourByType(farmType);
      }
    }

    // Priority 5: Team tour
    if (currentFarm && !completedTours.includes(TOUR_IDS.ADD_FARM_MEMBERS)) {
      return this.getAddFarmMembersTour();
    }

    // Priority 6: Financial tour
    if (currentFarm && !completedTours.includes(TOUR_IDS.FINANCIAL_INTRO)) {
      return this.getFinancialIntroTour();
    }

    return null;
  });

  /**
   * Computed signal for all available tours based on current state.
   * Returns only the recommended tour for strict context-awareness.
   * Users should only see tours relevant to their exact current state.
   */
  readonly availableTours = computed((): TourConfig[] => {
    const recommended = this.recommendedTour();
    return recommended ? [recommended] : [];
  });

  /**
   * Get the current recommended tour ID.
   * Useful for tracking which tour should be shown.
   */
  readonly recommendedTourId = computed((): TourId | null => {
    const tour = this.recommendedTour();
    return tour?.id as TourId | null;
  });

  // ===========================================================================
  // TOUR GETTERS
  // ===========================================================================

  /**
   * Get the "Create Your First Farm" tour configuration.
   * 3-step tour: Welcome → Navigate to farms → Create form guidance.
   * Uses navigateTo for cross-page navigation.
   */
  getNoFarmsTour(): TourConfig {
    const steps: TourStep[] = [
      {
        id: 'create-farm-1',
        name: 'welcome',
        highlights: [
          {
            id: 'branding-highlight',
            selector: '[data-tour="app-branding"]',
            padding: 8,
            glowColor: 'var(--primary-color)',
          },
        ],
        tooltip: {
          id: 'welcome-tooltip',
          targetSelector: '[data-tour="app-branding"]',
          title: 'tour.noFarms.welcomeTitle',
          description: 'tour.noFarms.welcomeDescription',
          position: 'bottom',
          showNavigation: true,
          showSkip: true,
          nextButtonText: 'tour.common.next',
        },
      },
      {
        id: 'create-farm-2',
        name: 'navigate-to-farms',
        highlights: [
          {
            id: 'nav-farms-highlight',
            selector: '[data-tour="nav-farms"]',
            padding: 6,
            glowColor: 'var(--primary-color)',
          },
        ],
        tooltip: {
          id: 'navigate-tooltip',
          targetSelector: '[data-tour="nav-farms"]',
          title: 'tour.noFarms.createFarmTitle',
          description: 'tour.noFarms.createFarmDescription',
          position: 'right',
          showNavigation: true,
          showSkip: true,
          nextButtonText: 'tour.common.next',
          prevButtonText: 'tour.common.previous',
        },
      },
      {
        id: 'create-farm-3',
        name: 'form-guidance',
        // Navigate to create-farm page before showing this step
        navigateTo: '/app/create-farm',
        waitForSelector: '[data-tour="farm-name-input"]',
        highlights: [
          {
            id: 'farm-name-highlight',
            selector: '[data-tour="farm-name-input"]',
            padding: 6,
            glowColor: 'var(--primary-color)',
          },
          {
            id: 'farm-type-highlight',
            selector: '[data-tour="farm-type-select"]',
            padding: 6,
            glowColor: 'var(--secondary-color)',
          },
        ],
        tooltip: {
          id: 'form-guidance-tooltip',
          targetSelector: '[data-tour="farm-name-input"]',
          title: 'tour.noFarms.formGuidanceTitle',
          description: 'tour.noFarms.formGuidanceDescription',
          position: 'right',
          showNavigation: true,
          showSkip: false,
          nextButtonText: 'tour.common.next',
          prevButtonText: 'tour.common.previous',
        },
      },
      {
        id: 'create-farm-4',
        name: 'farm-type-explanation',
        explanationCard: {
          id: 'farm-type-card',
          title: 'tour.noFarms.farmTypeCard.title',
          steps: [
            {
              stepNumber: 1,
              text: 'tour.noFarms.farmTypeCard.crop',
              targetSelector: '[data-tour="farm-type-select"]',
            },
            {
              stepNumber: 2,
              text: 'tour.noFarms.farmTypeCard.livestock',
              targetSelector: '[data-tour="farm-type-select"]',
            },
            {
              stepNumber: 3,
              text: 'tour.noFarms.farmTypeCard.mixed',
              targetSelector: '[data-tour="farm-type-select"]',
            },
          ],
          showConfirm: true,
          animateLines: true,
        },
      },
    ];

    return {
      id: TOUR_IDS.NO_FARMS_WELCOME,
      name: 'tour.noFarms.tourName',
      contextualSubtitle: 'tour.contextual.noFarmsSubtitle',
      steps,
      backdropOpacity: 0.7,
      keyboardNavigation: true,
      closeOnOutsideClick: false,
    };
  }

  /**
   * Get the farm selection tour configuration.
   * Guides users who have farms but haven't selected one.
   */
  getFarmSelectionTour(): TourConfig {
    const steps: TourStep[] = [
      {
        id: 'farm-selection-1',
        name: 'farm-selector',
        highlights: [
          {
            id: 'farm-selector-highlight',
            selector: '[data-tour="farm-selector"]',
            padding: 8,
            glowColor: 'var(--primary-color)',
          },
        ],
        tooltip: {
          id: 'farm-selector-tooltip',
          targetSelector: '[data-tour="farm-selector"]',
          title: 'tour.farmSelection.selectorTitle',
          description: 'tour.farmSelection.selectorDescription',
          position: 'bottom',
          showNavigation: true,
          showSkip: true,
          nextButtonText: 'tour.common.gotIt',
        },
        advanceOnElementClick: true,
      },
    ];

    return {
      id: TOUR_IDS.FARM_SELECTION,
      name: 'tour.farmSelection.tourName',
      contextualSubtitle: 'tour.contextual.farmSelectionSubtitle',
      steps,
      backdropOpacity: 0.6,
      keyboardNavigation: true,
      closeOnOutsideClick: true,
    };
  }

  /**
   * Get the crop farm setup tour configuration.
   * Uses explanation card with 4 steps pointing to key areas.
   */
  getCropFarmSetupTour(): TourConfig {
    const steps: TourStep[] = [
      {
        id: 'crop-farm-setup-1',
        name: 'overview',
        highlights: [
          {
            id: 'nav-fields-highlight',
            selector: '[data-tour="nav-fields"]',
            padding: 4,
            glowColor: 'var(--primary-color)',
          },
          {
            id: 'nav-crops-highlight',
            selector: '[data-tour="nav-crops"]',
            padding: 4,
            glowColor: 'var(--primary-color)',
          },
          {
            id: 'nav-equipment-highlight',
            selector: '[data-tour="nav-equipment"]',
            padding: 4,
            glowColor: 'var(--primary-color)',
          },
          {
            id: 'nav-tasks-highlight',
            selector: '[data-tour="nav-tasks"]',
            padding: 4,
            glowColor: 'var(--primary-color)',
          },
        ],
        explanationCard: {
          id: 'crop-setup-card',
          title: 'tour.cropFarm.cardTitle',
          steps: [
            {
              stepNumber: 1,
              text: 'tour.cropFarm.step1',
              targetSelector: '[data-tour="nav-fields"]',
              lineColor: 'var(--primary-color)',
            },
            {
              stepNumber: 2,
              text: 'tour.cropFarm.step2',
              targetSelector: '[data-tour="nav-crops"]',
              lineColor: 'var(--primary-color)',
            },
            {
              stepNumber: 3,
              text: 'tour.cropFarm.step3',
              targetSelector: '[data-tour="nav-equipment"]',
              lineColor: 'var(--primary-color)',
            },
            {
              stepNumber: 4,
              text: 'tour.cropFarm.step4',
              targetSelector: '[data-tour="nav-tasks"]',
              lineColor: 'var(--primary-color)',
            },
          ],
          position: 'center',
          showClose: true,
          showConfirm: true,
          confirmButtonText: 'tour.common.letsStart',
          animateLines: true,
          lineAnimationDuration: 500,
        },
      },
    ];

    return {
      id: TOUR_IDS.CROP_FARM_SETUP,
      name: 'tour.cropFarm.tourName',
      contextualSubtitle: 'tour.contextual.cropFarmSubtitle',
      steps,
      backdropOpacity: 0.6,
      keyboardNavigation: true,
      closeOnOutsideClick: false,
    };
  }

  /**
   * Get the livestock farm setup tour configuration.
   * Uses explanation card with 4 steps pointing to key areas.
   */
  getLivestockFarmSetupTour(): TourConfig {
    const steps: TourStep[] = [
      {
        id: 'livestock-farm-setup-1',
        name: 'overview',
        highlights: [
          {
            id: 'nav-livestock-highlight',
            selector: '[data-tour="nav-livestock"]',
            padding: 4,
            glowColor: 'var(--primary-color)',
          },
          {
            id: 'nav-buildings-highlight',
            selector: '[data-tour="nav-buildings"]',
            padding: 4,
            glowColor: 'var(--primary-color)',
          },
          {
            id: 'nav-inventory-highlight',
            selector: '[data-tour="nav-inventory"]',
            padding: 4,
            glowColor: 'var(--primary-color)',
          },
          {
            id: 'nav-tasks-highlight',
            selector: '[data-tour="nav-tasks"]',
            padding: 4,
            glowColor: 'var(--primary-color)',
          },
        ],
        explanationCard: {
          id: 'livestock-setup-card',
          title: 'tour.livestockFarm.cardTitle',
          steps: [
            {
              stepNumber: 1,
              text: 'tour.livestockFarm.step1',
              targetSelector: '[data-tour="nav-livestock"]',
              lineColor: 'var(--primary-color)',
            },
            {
              stepNumber: 2,
              text: 'tour.livestockFarm.step2',
              targetSelector: '[data-tour="nav-buildings"]',
              lineColor: 'var(--primary-color)',
            },
            {
              stepNumber: 3,
              text: 'tour.livestockFarm.step3',
              targetSelector: '[data-tour="nav-inventory"]',
              lineColor: 'var(--primary-color)',
            },
            {
              stepNumber: 4,
              text: 'tour.livestockFarm.step4',
              targetSelector: '[data-tour="nav-tasks"]',
              lineColor: 'var(--primary-color)',
            },
          ],
          position: 'center',
          showClose: true,
          showConfirm: true,
          confirmButtonText: 'tour.common.letsStart',
          animateLines: true,
          lineAnimationDuration: 500,
        },
      },
    ];

    return {
      id: TOUR_IDS.LIVESTOCK_FARM_SETUP,
      name: 'tour.livestockFarm.tourName',
      contextualSubtitle: 'tour.contextual.livestockFarmSubtitle',
      steps,
      backdropOpacity: 0.6,
      keyboardNavigation: true,
      closeOnOutsideClick: false,
    };
  }

  /**
   * Get the mixed farm setup tour configuration.
   * Uses explanation card with 4 steps pointing to key areas.
   */
  getMixedFarmSetupTour(): TourConfig {
    const steps: TourStep[] = [
      {
        id: 'mixed-farm-setup-1',
        name: 'overview',
        highlights: [
          {
            id: 'nav-fields-highlight',
            selector: '[data-tour="nav-fields"]',
            padding: 4,
            glowColor: 'var(--primary-color)',
          },
          {
            id: 'nav-livestock-highlight',
            selector: '[data-tour="nav-livestock"]',
            padding: 4,
            glowColor: 'var(--primary-color)',
          },
          {
            id: 'nav-buildings-highlight',
            selector: '[data-tour="nav-buildings"]',
            padding: 4,
            glowColor: 'var(--primary-color)',
          },
          {
            id: 'nav-dashboard-highlight',
            selector: '[data-tour="nav-dashboard"]',
            padding: 4,
            glowColor: 'var(--primary-color)',
          },
        ],
        explanationCard: {
          id: 'mixed-setup-card',
          title: 'tour.mixedFarm.cardTitle',
          steps: [
            {
              stepNumber: 1,
              text: 'tour.mixedFarm.step1',
              targetSelector: '[data-tour="nav-fields"]',
              lineColor: 'var(--primary-color)',
            },
            {
              stepNumber: 2,
              text: 'tour.mixedFarm.step2',
              targetSelector: '[data-tour="nav-livestock"]',
              lineColor: 'var(--primary-color)',
            },
            {
              stepNumber: 3,
              text: 'tour.mixedFarm.step3',
              targetSelector: '[data-tour="nav-buildings"]',
              lineColor: 'var(--primary-color)',
            },
            {
              stepNumber: 4,
              text: 'tour.mixedFarm.step4',
              targetSelector: '[data-tour="nav-dashboard"]',
              lineColor: 'var(--primary-color)',
            },
          ],
          position: 'center',
          showClose: true,
          showConfirm: true,
          confirmButtonText: 'tour.common.letsStart',
          animateLines: true,
          lineAnimationDuration: 500,
        },
      },
    ];

    return {
      id: TOUR_IDS.MIXED_FARM_SETUP,
      name: 'tour.mixedFarm.tourName',
      contextualSubtitle: 'tour.contextual.mixedFarmSubtitle',
      steps,
      backdropOpacity: 0.6,
      keyboardNavigation: true,
      closeOnOutsideClick: false,
    };
  }

  // ===========================================================================
  // FEATURE-SPECIFIC TOURS
  // ===========================================================================

  /**
   * Get the task workflow tour configuration.
   * Explains the task lifecycle: Create → Assign → Complete → Draft Action.
   */
  getTaskWorkflowTour(): TourConfig {
    const steps: TourStep[] = [
      {
        id: 'task-workflow-1',
        name: 'navigate-tasks',
        highlights: [
          {
            id: 'nav-tasks-highlight',
            selector: '[data-tour="nav-tasks"]',
            padding: 6,
            glowColor: 'var(--primary-color)',
          },
        ],
        tooltip: {
          id: 'navigate-tasks-tooltip',
          targetSelector: '[data-tour="nav-tasks"]',
          title: 'tour.taskWorkflow.step1Title',
          description: 'tour.taskWorkflow.step1Description',
          position: 'right',
          showNavigation: true,
          showSkip: true,
          nextButtonText: 'tour.common.next',
        },
      },
      {
        id: 'task-workflow-2',
        name: 'create-task',
        highlights: [
          {
            id: 'nav-tasks-highlight-2',
            selector: '[data-tour="nav-tasks"]',
            padding: 6,
            glowColor: 'var(--primary-color)',
          },
        ],
        tooltip: {
          id: 'create-task-tooltip',
          targetSelector: '[data-tour="nav-tasks"]',
          title: 'tour.taskWorkflow.step2Title',
          description: 'tour.taskWorkflow.step2Description',
          position: 'right',
          showNavigation: true,
          showSkip: true,
          nextButtonText: 'tour.common.next',
          prevButtonText: 'tour.common.previous',
        },
      },
      {
        id: 'task-workflow-3',
        name: 'complete-task',
        highlights: [
          {
            id: 'nav-tasks-highlight-3',
            selector: '[data-tour="nav-tasks"]',
            padding: 6,
            glowColor: 'var(--success-color)',
          },
        ],
        tooltip: {
          id: 'complete-task-tooltip',
          targetSelector: '[data-tour="nav-tasks"]',
          title: 'tour.taskWorkflow.step3Title',
          description: 'tour.taskWorkflow.step3Description',
          position: 'right',
          showNavigation: true,
          showSkip: true,
          nextButtonText: 'tour.common.next',
          prevButtonText: 'tour.common.previous',
        },
      },
      {
        id: 'task-workflow-4',
        name: 'draft-action',
        highlights: [
          {
            id: 'nav-drafts-highlight',
            selector: '[data-tour="nav-drafts"]',
            padding: 6,
            glowColor: 'var(--warning-color)',
          },
        ],
        tooltip: {
          id: 'draft-action-tooltip',
          targetSelector: '[data-tour="nav-drafts"]',
          title: 'tour.taskWorkflow.step4Title',
          description: 'tour.taskWorkflow.step4Description',
          position: 'right',
          showNavigation: true,
          showSkip: false,
          nextButtonText: 'tour.common.finish',
          prevButtonText: 'tour.common.previous',
        },
      },
    ];

    return {
      id: TOUR_IDS.TASK_WORKFLOW,
      name: 'tour.taskWorkflow.tourName',
      steps,
      backdropOpacity: 0.6,
      keyboardNavigation: true,
      closeOnOutsideClick: false,
    };
  }

  /**
   * Get the transactions intro tour configuration.
   * Introduces users to financial tracking features.
   */
  getTransactionsIntroTour(): TourConfig {
    const steps: TourStep[] = [
      {
        id: 'transactions-intro-1',
        name: 'navigate-transactions',
        highlights: [
          {
            id: 'nav-transactions-highlight',
            selector: '[data-tour="nav-transactions"]',
            padding: 6,
            glowColor: 'var(--primary-color)',
          },
        ],
        tooltip: {
          id: 'navigate-transactions-tooltip',
          targetSelector: '[data-tour="nav-transactions"]',
          title: 'tour.transactions.step1Title',
          description: 'tour.transactions.step1Description',
          position: 'right',
          showNavigation: true,
          showSkip: true,
          nextButtonText: 'tour.common.next',
        },
      },
      {
        id: 'transactions-intro-2',
        name: 'filter-transactions',
        highlights: [
          {
            id: 'nav-transactions-highlight-2',
            selector: '[data-tour="nav-transactions"]',
            padding: 6,
            glowColor: 'var(--primary-color)',
          },
        ],
        tooltip: {
          id: 'filter-transactions-tooltip',
          targetSelector: '[data-tour="nav-transactions"]',
          title: 'tour.transactions.step2Title',
          description: 'tour.transactions.step2Description',
          position: 'right',
          showNavigation: true,
          showSkip: true,
          nextButtonText: 'tour.common.next',
          prevButtonText: 'tour.common.previous',
        },
      },
      {
        id: 'transactions-intro-3',
        name: 'create-transaction',
        highlights: [
          {
            id: 'nav-transactions-highlight-3',
            selector: '[data-tour="nav-transactions"]',
            padding: 6,
            glowColor: 'var(--success-color)',
          },
        ],
        tooltip: {
          id: 'create-transaction-tooltip',
          targetSelector: '[data-tour="nav-transactions"]',
          title: 'tour.transactions.step3Title',
          description: 'tour.transactions.step3Description',
          position: 'right',
          showNavigation: true,
          showSkip: false,
          nextButtonText: 'tour.common.finish',
          prevButtonText: 'tour.common.previous',
        },
      },
    ];

    return {
      id: TOUR_IDS.TRANSACTIONS_INTRO,
      name: 'tour.transactions.tourName',
      steps,
      backdropOpacity: 0.6,
      keyboardNavigation: true,
      closeOnOutsideClick: false,
    };
  }

  /**
   * Get the AI assistant intro tour configuration.
   * Introduces users to the AI assistant feature.
   */
  getAiAssistantIntroTour(): TourConfig {
    const steps: TourStep[] = [
      {
        id: 'ai-nav',
        name: 'ai-navigation',
        highlights: [
          {
            id: 'h1',
            selector: '[data-tour="nav-ai"]',
            padding: 8,
          },
        ],
        tooltip: {
          id: 't1',
          targetSelector: '[data-tour="nav-ai"]',
          title: 'tour.aiAssistant.step1.title',
          description: 'tour.aiAssistant.step1.description',
          position: 'right',
          showNavigation: true,
        },
      },
      {
        id: 'ask-anything',
        name: 'ask-anything-button',
        highlights: [
          {
            id: 'h2',
            selector: '[data-tour="ask-anything-button"]',
            padding: 8,
          },
        ],
        tooltip: {
          id: 't2',
          targetSelector: '[data-tour="ask-anything-button"]',
          title: 'tour.aiAssistant.step2.title',
          description: 'tour.aiAssistant.step2.description',
          position: 'top',
          showNavigation: true,
        },
      },
    ];

    return {
      id: TOUR_IDS.AI_ASSISTANT_INTRO,
      name: 'tour.aiAssistant.tourName',
      steps,
      backdropOpacity: 0.6,
      keyboardNavigation: true,
      closeOnOutsideClick: false,
    };
  }

  /**
   * Get the add farm members tour configuration.
   * Guides users through inviting team members.
   */
  getAddFarmMembersTour(): TourConfig {
    const steps: TourStep[] = [
      {
        id: 'team-nav',
        name: 'team-navigation',
        highlights: [
          {
            id: 'h1',
            selector: '[data-tour="nav-team"]',
            padding: 8,
          },
        ],
        tooltip: {
          id: 't1',
          targetSelector: '[data-tour="nav-team"]',
          title: 'tour.farmMembers.step1.title',
          description: 'tour.farmMembers.step1.description',
          position: 'right',
          showNavigation: true,
        },
      },
      {
        id: 'invite-button',
        name: 'invite-button',
        highlights: [
          {
            id: 'h2',
            selector: '[data-tour="team-invite-button"]',
            padding: 8,
          },
        ],
        tooltip: {
          id: 't2',
          targetSelector: '[data-tour="team-invite-button"]',
          title: 'tour.farmMembers.step2.title',
          description: 'tour.farmMembers.step2.description',
          position: 'bottom',
          showNavigation: true,
        },
      },
    ];

    return {
      id: TOUR_IDS.ADD_FARM_MEMBERS,
      name: 'tour.farmMembers.tourName',
      steps,
      backdropOpacity: 0.6,
      keyboardNavigation: true,
      closeOnOutsideClick: false,
    };
  }

  /**
   * Get the financial intro tour configuration.
   * Comprehensive introduction to financial features.
   */
  getFinancialIntroTour(): TourConfig {
    const steps: TourStep[] = [
      {
        id: 'transactions-nav',
        name: 'transactions-navigation',
        highlights: [
          {
            id: 'h1',
            selector: '[data-tour="nav-transactions"]',
            padding: 8,
          },
        ],
        tooltip: {
          id: 't1',
          targetSelector: '[data-tour="nav-transactions"]',
          title: 'tour.financial.step1.title',
          description: 'tour.financial.step1.description',
          position: 'right',
          showNavigation: true,
        },
      },
      {
        id: 'filters',
        name: 'transaction-filters',
        highlights: [
          {
            id: 'h2',
            selector: '[data-tour="transaction-filters"]',
            padding: 8,
          },
        ],
        tooltip: {
          id: 't2',
          targetSelector: '[data-tour="transaction-filters"]',
          title: 'tour.financial.step2.title',
          description: 'tour.financial.step2.description',
          position: 'bottom',
          showNavigation: true,
        },
      },
      {
        id: 'reports-nav',
        name: 'reports-navigation',
        highlights: [
          {
            id: 'h3',
            selector: '[data-tour="nav-reports"]',
            padding: 8,
          },
        ],
        tooltip: {
          id: 't3',
          targetSelector: '[data-tour="nav-reports"]',
          title: 'tour.financial.step3.title',
          description: 'tour.financial.step3.description',
          position: 'right',
          showNavigation: true,
        },
      },
    ];

    return {
      id: TOUR_IDS.FINANCIAL_INTRO,
      name: 'tour.financial.tourName',
      steps,
      backdropOpacity: 0.6,
      keyboardNavigation: true,
      closeOnOutsideClick: false,
    };
  }

  // ===========================================================================
  // PRIVATE HELPER METHODS
  // ===========================================================================

  /**
   * Get the setup tour ID for a given farm type.
   * Returns null if no setup tour is defined for the farm type.
   */
  private getSetupTourIdByType(farmType: FarmType): string | null {
    switch (farmType) {
      case FarmType.CROP:
        return TOUR_IDS.CROP_FARM_SETUP;
      case FarmType.LIVESTOCK:
        return TOUR_IDS.LIVESTOCK_FARM_SETUP;
      case FarmType.MIXED:
        return TOUR_IDS.MIXED_FARM_SETUP;
      default:
        return null;
    }
  }

  /**
   * Get the setup tour configuration for a given farm type.
   * Returns null if no setup tour is defined for the farm type.
   */
  private getSetupTourByType(farmType: FarmType): TourConfig | null {
    switch (farmType) {
      case FarmType.CROP:
        return this.getCropFarmSetupTour();
      case FarmType.LIVESTOCK:
        return this.getLivestockFarmSetupTour();
      case FarmType.MIXED:
        return this.getMixedFarmSetupTour();
      default:
        return null;
    }
  }

  // ===========================================================================
  // UTILITY METHODS
  // ===========================================================================

  /**
   * Get a tour by its ID.
   * Returns null if the tour ID is not recognized.
   */
  getTourById(tourId: TourId): TourConfig | null {
    switch (tourId) {
      case TOUR_IDS.NO_FARMS_WELCOME:
        return this.getNoFarmsTour();
      case TOUR_IDS.FARM_SELECTION:
        return this.getFarmSelectionTour();
      case TOUR_IDS.CROP_FARM_SETUP:
        return this.getCropFarmSetupTour();
      case TOUR_IDS.LIVESTOCK_FARM_SETUP:
        return this.getLivestockFarmSetupTour();
      case TOUR_IDS.MIXED_FARM_SETUP:
        return this.getMixedFarmSetupTour();
      case TOUR_IDS.TASK_WORKFLOW:
        return this.getTaskWorkflowTour();
      case TOUR_IDS.TRANSACTIONS_INTRO:
        return this.getTransactionsIntroTour();
      case TOUR_IDS.AI_ASSISTANT_INTRO:
        return this.getAiAssistantIntroTour();
      case TOUR_IDS.ADD_FARM_MEMBERS:
        return this.getAddFarmMembersTour();
      case TOUR_IDS.FINANCIAL_INTRO:
        return this.getFinancialIntroTour();
      default:
        return null;
    }
  }

  /**
   * Check if a tour is relevant for the current farm state.
   */
  isTourRelevant(tourId: TourId): boolean {
    const hasFarms = this.farmStore.hasFarms();
    const currentFarm = this.farmStore.currentFarm();

    switch (tourId) {
      case TOUR_IDS.NO_FARMS_WELCOME:
        return !hasFarms;

      case TOUR_IDS.FARM_SELECTION:
        return hasFarms && !currentFarm;

      case TOUR_IDS.CROP_FARM_SETUP:
        return hasFarms && currentFarm?.type === FarmType.CROP;

      case TOUR_IDS.LIVESTOCK_FARM_SETUP:
        return hasFarms && currentFarm?.type === FarmType.LIVESTOCK;

      case TOUR_IDS.MIXED_FARM_SETUP:
        return hasFarms && currentFarm?.type === FarmType.MIXED;

      // Feature-specific tours are always relevant when farm context exists
      case TOUR_IDS.TASK_WORKFLOW:
      case TOUR_IDS.TRANSACTIONS_INTRO:
      case TOUR_IDS.AI_ASSISTANT_INTRO:
      case TOUR_IDS.ADD_FARM_MEMBERS:
      case TOUR_IDS.FINANCIAL_INTRO:
        return hasFarms && !!currentFarm;

      default:
        return false;
    }
  }

  /**
   * Get all tour IDs as an array.
   */
  getAllTourIds(): TourId[] {
    return Object.values(TOUR_IDS);
  }
}
